<template>
    <div v-if="isLoading" class="app-wrapper">
        <app-loader />
    </div>
    <div v-else-if="!user.id" class="app-wrapper" :class="classes">
        <div class="app-container">
            <router-view name="sidebar" />
            <router-view class="app-content" />
        </div>
        <toast ref="toast" />
        <modal-security />
    </div>
    <div v-else class="app-wrapper" :class="classes">
        <div class="app-container">
            <div class="app-body">
                <router-view
                    v-if="!$route.meta.hasOwnProperty('sidebar') || $route.meta.sidebar"
                    name="sidebar"
                />
                <router-view class="app-content" />
            </div>
        </div>

        <toast ref="toast" />
        <session-expired />
        <away-status-modal />
        <ten-d-l-c-disabled-banner />

        <template v-if="subscription">
            <template v-if="subscription.plan.active">
                <add-credits-modal
                    v-if="!isOnTrial || addFundsShown"
                    :show="addFundsShown"
                    @closed="hideAddFunds"
                />
            </template>
            <template v-else>
                <early-upgrade-modal
                    v-if="isOnTrial || earlyUpgradeShown"
                    :show="earlyUpgradeShown"
                    @closed="hideEarlyUpgrade"
                />
                <add-funds-modal
                    v-if="!isOnTrial || addFundsShown"
                    :show="addFundsShown"
                    @closed="hideAddFunds"
                />
            </template>
        </template>

        <div
            v-if="retently_id"
            id="retently-survey-embed"
            :data-href="`https://app.retently.com/api/remote/tracking/${retently_id}`"
            data-rel="dialog"
            data-campaign="regular"
            :data-email="user.email"
            :data-firstname="user.firstName"
            :data-lastname="user.lastName"
            :data-company="organization && organization.name ? organization.name : ''"
            :data-prop-Organization_ID="organization && organization.id ? organization.id : 0"
            :data-prop-User_ID="user.id"
        />

        <create-payment-methods
            :show="paymentMethodsShown"
            :backup="paymentMethodsBackup"
            @closed="hidePaymentMethods"
        />
        <call-widget v-if="showCall" />
        <change-role-request
            :show="showModalChangeRoleRequest"
            @closed="handleChangeRoleRequestClosed"
        />
        <modal-security />

        <trial-limit-reached-modal />

        <portal-target name="root-app" />
    </div>
</template>

<script>
import { inject } from 'vue';
import { mapState } from 'pinia';
import stores from 'store/modules';
import { uniq } from 'lodash';

import addFundsMixin from 'mixins/add-funds';
import earlyUpgradeMixin from 'mixins/early-upgrade';
import echoConversationMixin from 'mixins/echo/conversation';
import echoInboxMixin from 'mixins/echo/inbox';
import echoMessageMixin from 'mixins/echo/message';
import echoOrganizationMixin from 'mixins/echo/organization';
import echoUserMixin from 'mixins/echo/user';
import numberMixin from 'mixins/number';
import OrganizationMixin from 'mixins/organization';
import userMixin from 'mixins/user';
import AppVueMixin from './mixins/app-vue';
import AddCreditsModal from 'components/Billing/AddCreditsModal.vue';
import AddFundsModal from 'components/Billing/AddFundsModal.vue';
import EarlyUpgradeModal from 'components/Billing/EarlyUpgradeModal.vue';
import CallWidget from 'components/VoiceOrVideo/CallWidget.vue';

import ModalSecurity from 'components/Security/ModalSecurity';
import CreatePaymentMethods from './../Settings/Billing/Overview/PaymentMethods/Create.vue';
import ChangeRoleRequest from './../Settings/Personal/Settings/ChangeRoleRequest';
import TrialLimitReachedModal from 'views/Conversations/Modal/TrialLimitReachedModal/TrialLimitReachedModal.vue';

export default {
    components: {
        TrialLimitReachedModal,
        AddFundsModal,
        AddCreditsModal,
        EarlyUpgradeModal,
        CallWidget,
        ChangeRoleRequest,
        CreatePaymentMethods,
        ModalSecurity,
    },

    mixins: [
        addFundsMixin,
        earlyUpgradeMixin,
        echoConversationMixin,
        echoInboxMixin,
        echoMessageMixin,
        echoOrganizationMixin,
        echoUserMixin,
        numberMixin,
        OrganizationMixin,
        userMixin,
        AppVueMixin,
    ],

    data() {
        return {
            shortCut: {
                modifier: 0,
                action: 0,
                timeout: null,
            },
            cannyIdentified: false,
            retently_id: window.Config.retently.tracking_id,
            isBoxShowing: this.$cookie.get('dlcBanner') !== 'false',
            showUpgradePlanTest: true,
            showDowngradePlanTest: false,
            initialized: false,
        };
    },

    computed: {
        isLoading() {
            return Boolean(
                this.user === null ||
                    this.billingSourcesLoading ||
                    this.loadingMain ||
                    this.dlsLimitsLoading ||
                    (this.user && this.user.isLoggedIn && this.billingPlansLoading)
            );
        },

        isLoadingEcho() {
            return Boolean(
                this.user === null ||
                    this.billingSourcesLoading ||
                    this.loadingMain ||
                    this.dlsLimitsLoading ||
                    (this.user && this.user.isLoggedIn && this.billingPlansLoading)
            );
        },

        classes() {
            if (!this.$route.name) {
                return [];
            }

            let route = this.$route.name.split('.');

            return uniq([route[0], route.join('-')]);
        },

        integrationDisconnected() {
            return this.user.integrationDisconnected === 1;
        },

        ...mapState(stores.User, [
            'user',
            'integrationDisconnected',
            'loadingMain',
            'showModalChangeRoleRequest',
            'config',
            'userPermissions',
        ]),
        ...mapState(stores.Subscriptions, ['subscription', 'upgradePlanShown']),
        ...mapState(stores.Toast, ['toast']),
        ...mapState(stores.Members, ['members']),
        ...mapState(stores.Organization, ['organization', 'featureFlags']),
        ...mapState(stores.Number, ['number', 'numbers', 'hasNumber', 'numberVendor']),
        ...mapState(stores.AutoRecharge, ['autorechargeInfo', 'hasAutorechargeFailureStatus']),
        ...mapState(stores.BillingSources, [
            'billingSources',
            'billingSourcesSimple',
            'billingSourcesLoading',
            'billingPlans',
            'billingPlansLoading',
        ]),
        ...mapState(stores.Conversations, ['conversationsArray', 'conversationsOrder']),
        ...mapState(stores.DLCProfile, ['dlsLimitsLoading']),
        ...mapState(stores.Inboxes, ['inboxesArray']),
    },

    watch: {
        toast(toast) {
            if (toast === null) return;

            this.$refs.toast.showToast(toast.type, toast.message);
        },
    },

    methods: {
        hideAddFunds() {
            this.$router.replace({
                query: '',
            });
            this.$store.dispatch('hideAddFunds');
        },
    },

    mounted() {
        this.$rootEvents.$on('handleChangeRoleRequestOpen', this.handleChangeRoleRequestOpen);
        this.$rootEvents.$on('handleChangeRoleRequestClosed', this.handleChangeRoleRequestClosed);

        this.initUserEcho();
        this.initInboxesEcho();
        this.initUserInboxEcho();
        this.initOrganizationEcho();
    },

    beforeUnmount() {
        this.$rootEvents.$off('handleChangeRoleRequestOpen', this.handleChangeRoleRequestOpen);
        this.$rootEvents.$off('handleChangeRoleRequestClosed', this.handleChangeRoleRequestClosed);

        this.resetUserEcho();
        this.resetInboxesEcho();
        this.resetUserInboxEcho();
        this.resetOrganizationEcho();
    },
};
</script>

<style lang="scss" scoped>
@import './styles';
</style>
